import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SCORED_SECTIONS, BANDS, getBand } from "@/lib/trcaSchema";
import { CheckCircle2, Calendar, ArrowRight, Home, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function TrcaResults() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Try session first (just-submitted case)
    const cached = sessionStorage.getItem("twin.trca.lastResult");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.id === id) { setData(parsed); return; }
      } catch {}
    }
    if (!user) {
      setErr("Please sign in to view this result.");
      return;
    }
    (async () => {
      try {
        if (user.role === "admin") {
          const { data } = await api.get(`/trca/submissions/${id}`);
          setData(data);
        } else {
          const { data } = await api.get(`/trca/me/result`);
          if (data?.id !== id) {
            setErr("This result is not available on your account.");
          } else {
            setData(data);
          }
        }
      } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message); }
    })();
  }, [id, user]);

  if (err) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="card-flat p-8 max-w-md text-center">
        <div className="text-warn font-display font-bold text-lg mb-2">Unable to load results</div>
        <div className="text-sm text-ink-muted mb-6">{err}</div>
        <Link to="/" className="text-brand hover:underline text-sm">← Back to home</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-ink-muted text-sm uppercase tracking-wider">Loading…</div>;

  const band = getBand(data.total_score);

  return (
    <div className="min-h-screen bg-background" data-testid="trca-results">
      {/* Header */}
      <div className="border-b border-ink-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand grid place-items-center text-white font-display font-bold tracking-tight rounded-sm">T</div>
            <div className="leading-none">
              <div className="font-display font-bold text-[15px] tracking-tight">TWIN</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted mt-1">Tender Intelligence</div>
            </div>
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 lg:py-16 animate-fade-up">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Assessment results</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
          Thank you, {data.name.split(" ")[0]}.
        </h1>
        <p className="text-ink-muted mt-3 max-w-2xl">
          Your Tender Readiness Capability Assessment is complete. Our team will reach out within 2 business days to discuss your results.
        </p>

        {/* Score + Band */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
          <div className="card-flat p-8 lg:col-span-1 flex flex-col items-center justify-center" data-testid="trca-total-score">
            <div className="stat-label">Total score</div>
            <div className="font-num text-7xl font-bold mt-3" style={{ color: band.color }}>{data.total_score}</div>
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mt-2">/ 100</div>
          </div>
          <div className="card-flat p-8 lg:col-span-2" data-testid="trca-band">
            <div className="flex items-center gap-3 mb-3">
              <Award className="w-5 h-5" style={{ color: band.color }} />
              <div className="stat-label">Readiness band</div>
            </div>
            <div className="font-display text-4xl font-bold tracking-tight" style={{ color: band.color }}>{band.name}</div>
            <p className="text-sm text-ink leading-relaxed mt-3">{band.desc}</p>

            <div className="mt-6 pt-6 border-t border-ink-line">
              <div className="flex items-center justify-between mb-2 text-[11px] uppercase tracking-wider text-ink-muted">
                <span>0</span><span>30</span><span>60</span><span>85</span><span>100</span>
              </div>
              <div className="relative h-3 bg-secondary rounded-sm overflow-hidden flex">
                {BANDS.map((b) => (
                  <div key={b.name} className="h-full" style={{ width: `${(b.max - b.min + 1)}%`, background: b.color, opacity: b.name === band.name ? 1 : 0.25 }} />
                ))}
                <div className="absolute top-0 bottom-0 w-0.5 bg-ink" style={{ left: `${data.total_score}%` }} />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[10px] uppercase tracking-wider text-ink-muted">
                {BANDS.map((b) => <span key={b.name} style={{ color: b.name === band.name ? band.color : "" }}>{b.name}</span>)}
              </div>
            </div>
          </div>
        </div>

        {/* Section breakdown */}
        <div className="card-flat mt-6" data-testid="trca-section-breakdown">
          <div className="px-6 py-4 border-b border-ink-line">
            <div className="stat-label">Section breakdown</div>
            <div className="font-display font-bold text-lg tracking-tight mt-1">Your strengths and gaps</div>
          </div>
          <div className="divide-y divide-ink-line">
            {SCORED_SECTIONS.map((s) => {
              const score = data.section_scores?.[s.key] ?? 0;
              const pct = (score / s.max) * 100;
              return (
                <div key={s.key} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">{s.title}</div>
                    <div className="font-num text-sm font-bold">{score} <span className="text-ink-muted font-normal">/ {s.max}</span></div>
                  </div>
                  <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                    <div className="h-full bg-brand transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Next steps */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-flat p-6 flex items-start gap-4">
            <Calendar className="w-5 h-5 text-brand mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-bold text-base tracking-tight">We'll reach out shortly</div>
              <p className="text-sm text-ink-muted mt-1.5">Our consulting team will email <span className="font-num text-ink">{data.email}</span> within 2 business days with tailored next steps.</p>
            </div>
          </div>
          <div className="card-flat p-6 bg-brand text-white">
            <CheckCircle2 className="w-5 h-5 text-warn mb-3" />
            <div className="font-display font-bold text-base tracking-tight">Want to talk sooner?</div>
            <p className="text-sm text-white/80 mt-1.5">Book a 30-minute consultation to walk through your TRCA results live.</p>
            <a
              href="mailto:solutions@tendstrat.com?subject=TRCA%20Consultation%20Request"
              data-testid="trca-book-consult"
              className="inline-flex items-center gap-2 mt-4 bg-warn hover:bg-warn/90 text-white px-4 py-2 text-sm font-medium rounded-sm transition-colors"
            >
              Book a consultation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-3 text-sm">
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 border border-ink-line rounded-sm hover:bg-secondary transition-colors">
            <Home className="w-4 h-4" /> Return home
          </Link>
        </div>
      </div>
    </div>
  );
}
